import { z } from 'zod';
import { getKvpsDealerId, ThingNameSchema } from 'utils/thingName';
import { IStage } from 'data/stage';
import { EXHIBITION_STAGE } from 'utils/ui/stage';
import { localAveTabId } from 'utils/localAveTabId';
import logger from 'utils/log';
import { getFreshIdToken } from './idToken';
import { JwtTokenSchema } from './schemas';
import {
  getLocalAveContextTokenCallback,
  type TLocalAveContextTokenCallback,
  type TLocalAveContextTokenRequest,
} from './localAveContextTokenCallback';

/** Called by the 3DWS deck whenever it needs a viewer context token for a local AVE connection. */
export type ContextTokenProvider = () => Promise<string>;

export type TContextTokenErrorKind = 'auth' | 'forbidden' | 'network' | 'malformed';

/** A failed context-token request. The message carries at most the HTTP status, never a token. */
export class ContextTokenError extends Error {
  readonly status?: number;
  readonly kind: TContextTokenErrorKind;

  constructor(kind: TContextTokenErrorKind, message: string, status?: number) {
    super(message);
    this.name = 'ContextTokenError';
    this.kind = kind;
    this.status = status;
  }
}

export const avpDevicesContextTokenEndpoint: Record<IStage, string> = {
  test: 'https://devices.develop.avp.tech/context-token/viaIdToken',
  dev: 'https://devices.develop.avp.tech/context-token/viaIdToken',
  prelive: 'https://devices.preprod.avp.tech/context-token/viaIdToken',
  prod: 'https://devices.avp.tech/context-token/viaIdToken',
};

export const ContextTokenResponseSchema = z.object({ contextToken: JwtTokenSchema });

const CONTEXT_TOKEN_TIMEOUT_MS = 10_000;
const MAX_LIFETIME_S = 2 * 60 * 60;
const REFRESH_MARGIN_MS = 5 * 60 * 1000;
const LOG_PREFIX = '[localAveContextToken]';
const CALLBACK_FAILED_MESSAGE = 'Context token callback failed';
const CALLBACK_TIMEOUT_MESSAGE = 'Context token callback timed out';
const CALLBACK_MALFORMED_MESSAGE = 'Context token callback returned a malformed token';
const REQUEST_TIMEOUT_MESSAGE = 'Context token request timed out';

const ContextTokenClaimsSchema = z.object({ exp: z.number().optional(), iat: z.number().optional() }).passthrough();
type TContextTokenClaims = z.infer<typeof ContextTokenClaimsSchema>;

function kindFromStatus(status: number): TContextTokenErrorKind {
  if (status === 401) return 'auth';
  if (status === 403) return 'forbidden';
  return 'network';
}

/**
 * Fetches a viewer context token for one local AVE.
 * Uses the global `fetch` (like `tvm.ts`) so the response body never enters the recorder's api lane.
 *
 * @throws {ContextTokenError} on a non-ok status, a timeout, a network failure or a malformed body
 */
export async function fetchContextToken(
  idToken: string,
  stage: IStage,
  kvpsDealerId: string,
  thingName: string,
  viewerSessionId: string
): Promise<string> {
  const query = new URLSearchParams({ thingName, viewerSessionId });
  const url = `${avpDevicesContextTokenEndpoint[stage]}/${encodeURIComponent(kvpsDealerId)}?${query.toString()}`;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CONTEXT_TOKEN_TIMEOUT_MS);
  try {
    return await requestContextToken(url, idToken, controller.signal);
  } finally {
    clearTimeout(timer);
  }
}

async function requestContextToken(url: string, idToken: string, signal: AbortSignal): Promise<string> {
  const response = await fetch(url, { headers: { Authorization: `Bearer ${idToken}` }, signal }).catch(() => {
    throw new ContextTokenError('network', 'Context token request failed');
  });
  const { status } = response;
  if (!response.ok) {
    throw new ContextTokenError(kindFromStatus(status), `Context token request failed with status ${status}`, status);
  }
  const json: unknown = await response.json().catch(() => {
    throw new ContextTokenError(
      signal.aborted ? 'network' : 'malformed',
      `Context token response unreadable (status ${status})`,
      status
    );
  });
  const parsed = ContextTokenResponseSchema.safeParse(json);
  if (!parsed.success) {
    throw new ContextTokenError('malformed', `Malformed context token response (status ${status})`, status);
  }
  return parsed.data.contextToken;
}

function decodeBase64Url(segment: string): string {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  return atob(base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '='));
}

function readClaims(token: string): TContextTokenClaims | null {
  const payload = token.split('.')[1] ?? '';
  let decoded: unknown;
  try {
    decoded = JSON.parse(decodeBase64Url(payload));
  } catch {
    return null;
  }
  const claims = ContextTokenClaimsSchema.safeParse(decoded);
  return claims.success ? claims.data : null;
}

/**
 * When a cached context token stops being usable, on OUR clock.
 * - `exp` and `iat`: lifetime `exp - iat` (at most 2 h) from `fetchedAtMs`, so a skewed local clock
 *   can neither shorten nor stretch it. Relies on the endpoint minting a fresh token per request.
 * - `exp` only: the absolute `exp`, clamped to `fetchedAtMs + 2 h`.
 * - neither: `fetchedAtMs + 2 h`.
 */
export function tokenExpiresAtMs(token: string, fetchedAtMs: number): number {
  const maxExpiresAtMs = fetchedAtMs + MAX_LIFETIME_S * 1000;
  const claims = readClaims(token);
  if (claims?.exp !== undefined && claims.iat !== undefined) {
    return relativeExpiresAtMs(claims.exp, claims.iat, fetchedAtMs);
  }
  if (claims?.exp !== undefined) return Math.min(claims.exp * 1000, maxExpiresAtMs);
  return maxExpiresAtMs;
}

/** `fetchedAtMs` plus the token's own lifetime `exp - iat`, at most 2 h. */
function relativeExpiresAtMs(exp: number, iat: number, fetchedAtMs: number): number {
  return fetchedAtMs + Math.min(Math.max(exp - iat, 0), MAX_LIFETIME_S) * 1000;
}

/**
 * When a context token from the integrator callback stops being usable, on OUR clock: the earlier
 * of the absolute `exp` and the relative lifetime from `fetchedAtMs`. The absolute bound caps a
 * re-served old token when our clock is in sync; the relative bound keeps a fresh token from
 * outliving its lifetime when our clock lags the issuer. A token without `exp` and `iat` never
 * reaches this point (`requestContextTokenFromCallback` rejects it), so it counts as expired.
 */
function callbackTokenExpiresAtMs(token: string, fetchedAtMs: number): number {
  const claims = readClaims(token);
  if (claims?.exp === undefined || claims.iat === undefined) return fetchedAtMs;
  return Math.min(claims.exp * 1000, relativeExpiresAtMs(claims.exp, claims.iat, fetchedAtMs));
}

function parseCallbackToken(value: unknown): string {
  const parsed = JwtTokenSchema.safeParse(value);
  const claims = parsed.success ? readClaims(parsed.data) : null;
  if (!parsed.success || claims?.exp === undefined || claims.iat === undefined) {
    throw new ContextTokenError('malformed', CALLBACK_MALFORMED_MESSAGE);
  }
  return parsed.data;
}

/**
 * Settles with `work`, or rejects with `ContextTokenError('network', message)` once 10 s pass.
 * The timer is always cleared. A late result of `work` is ignored by the caller.
 *
 * @throws {ContextTokenError} on the deadline, or whatever `work` rejects with
 */
async function withContextTokenDeadline<T>(work: Promise<T>, message: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(new ContextTokenError('network', message)), CONTEXT_TOKEN_TIMEOUT_MS);
  });
  try {
    return await Promise.race([work, deadline]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Asks the integrator callback for a context token, with the same 10 s timeout as the endpoint.
 * The callback's value and error never reach a message: rejection, sync throw or timeout is
 * `network`; anything but a JWT carrying both `exp` and `iat` is `malformed`.
 *
 * @throws {ContextTokenError}
 */
async function requestContextTokenFromCallback(
  callback: TLocalAveContextTokenCallback,
  request: TLocalAveContextTokenRequest
): Promise<string> {
  const call = Promise.resolve()
    .then(() => callback(request))
    .then(
      (value: unknown) => value,
      () => {
        throw new ContextTokenError('network', CALLBACK_FAILED_MESSAGE);
      }
    );
  const value = await withContextTokenDeadline(call, CALLBACK_TIMEOUT_MESSAGE);
  return parseCallbackToken(value);
}

/**
 * Fetches a context token with the signed-in user's ID token. Returns when the fetch started, so
 * the lifetime runs on our clock. The caller bounds the whole leg, refresh included, with the deadline.
 *
 * @throws {ContextTokenError}
 */
async function requestContextTokenViaIdToken(
  kvpsDealerId: string,
  thingName: string,
  viewerSessionId: string
): Promise<{ token: string; fetchedAtMs: number }> {
  const idToken = await getFreshIdToken().catch(() => {
    throw new ContextTokenError('auth', 'ID token not available for the context token request');
  });
  const fetchedAtMs = Date.now();
  const token = await fetchContextToken(idToken, EXHIBITION_STAGE, kvpsDealerId, thingName, viewerSessionId);
  return { token, fetchedAtMs };
}

/**
 * Creates the context-token provider for one local AVE (`thingName`). Never throws: an invalid
 * thingName rejects each call with `kind: 'malformed'` instead, so a legacy device id cannot crash
 * a render. The token lives in this closure only. Concurrent calls share one request, and a token
 * is reused until 5 minutes before its lifetime ends. With an integrator callback registered
 * (`setLocalAveContextTokenCallback`), the callback replaces the ID-token request on a cache miss.
 *
 * `viewerSessionId` is this tab's `localAveTabId`, the id own-device recognition compares against.
 */
export function createLocalAveContextTokenProvider(
  thingName: string
): ContextTokenProvider & { readonly viewerSessionId: string } {
  const viewerSessionId = localAveTabId;
  let cached: { token: string; expiresAtMs: number } | null = null;
  let inFlight: Promise<string> | null = null;

  const load = async (): Promise<string> => {
    const parsedThingName = ThingNameSchema.safeParse(thingName);
    if (!parsedThingName.success) {
      throw new ContextTokenError('malformed', 'Context token requested for an invalid thingName');
    }
    const kvpsDealerId = getKvpsDealerId(parsedThingName.data);
    const callback = getLocalAveContextTokenCallback();
    if (callback) {
      const callbackFetchedAtMs = Date.now();
      const callbackToken = await requestContextTokenFromCallback(callback, {
        thingName: parsedThingName.data,
        kvpsDealerId,
        viewerSessionId,
      });
      cached = { token: callbackToken, expiresAtMs: callbackTokenExpiresAtMs(callbackToken, callbackFetchedAtMs) };
      return callbackToken;
    }
    const { token, fetchedAtMs } = await withContextTokenDeadline(
      requestContextTokenViaIdToken(kvpsDealerId, parsedThingName.data, viewerSessionId),
      REQUEST_TIMEOUT_MESSAGE
    );
    cached = { token, expiresAtMs: tokenExpiresAtMs(token, fetchedAtMs) };
    return token;
  };

  const provider = (): Promise<string> => {
    if (cached && Date.now() < cached.expiresAtMs - REFRESH_MARGIN_MS) return Promise.resolve(cached.token);
    if (!inFlight) {
      logger.debug(`${LOG_PREFIX} requesting a context token`, { thingName, viewerSessionId });
      inFlight = load()
        .catch((error: unknown) => {
          logger.debug(`${LOG_PREFIX} context token request failed`, { thingName, viewerSessionId });
          throw error;
        })
        .finally(() => {
          inFlight = null;
        });
    }
    return inFlight;
  };

  return Object.assign(provider, { viewerSessionId });
}
